/**
 * Shared campaign-outcome-summary shape (campaign-sale / offer-approval).
 *
 * Campaign confirmation (POST /offers/:id/approve) and the campaign sale
 * endpoint (POST /offers/:id/mark-sold) each resolve a set of nośnik+window
 * pairs independently and best-effort, and each report per-outcome results.
 * Rather than modelling that as two parallel, independently-drifting
 * schemas, both reuse ONE outcome item shape: confirmation populates
 * 'newly_reserved' / 'raised_from_preliminary' / 'already_secured' /
 * 'queued' / 'not_secured'; sale populates 'sold' / 'already_sold' /
 * 'skipped_not_accepted' / 'not_closed' — each endpoint uses only the outcome
 * kinds meaningful to its own action (offer-approval / "Offer Approval
 * Endpoint", campaign-sale / "Campaign sale endpoint").
 *
 * A caller's interface can be deployed ahead of a server that adds a new
 * outcome member (action-breakdown / "An unrecognised outcome never presents
 * a committed action as failed"). A plain `z.discriminatedUnion` throws on an
 * outcome literal it does not declare, which would make a committed action
 * read as a failed request. `CampaignOutcomeItemSchema` is therefore a
 * `z.union` of the known discriminated union plus a catch-all: any literal
 * outside the known set parses into a neutral `'unrecognized'` entry that
 * still carries its nośnik and window, rather than throwing.
 */
import { z } from 'zod';
import { OfferSchema } from './offer.js';
import { ReservationStatusSchema } from './reservation-status.js';
import { MinWindowDaysSchema, SelectedDaysSchema } from './reservation-batch.js';

const CampaignOutcomeCellSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
});

const KNOWN_CAMPAIGN_OUTCOMES = new Set([
  'newly_reserved',
  'raised_from_preliminary',
  'already_secured',
  'queued',
  'not_secured',
  'sold',
  'already_sold',
  'skipped_not_accepted',
  'not_closed',
]);

const KnownCampaignOutcomeItemSchema = z.discriminatedUnion('outcome', [
  CampaignOutcomeCellSchema.extend({ outcome: z.literal('newly_reserved') }),
  CampaignOutcomeCellSchema.extend({ outcome: z.literal('raised_from_preliminary') }),
  CampaignOutcomeCellSchema.extend({ outcome: z.literal('already_secured') }),
  CampaignOutcomeCellSchema.extend({
    outcome: z.literal('queued'),
    position: z.number().int().positive(),
  }),
  CampaignOutcomeCellSchema.extend({
    outcome: z.literal('not_secured'),
    reason: z.string(),
    // Required-vs-selected day counts for a minimum-reservation-window
    // breach — reused from the batch-reserve conflict cell's own
    // declaration (action-breakdown / "One outcome vocabulary across
    // fan-out actions") rather than restated here. Optional: a reason the
    // interface has no specific wording for carries neither.
    minWindowDays: MinWindowDaysSchema,
    selectedDays: SelectedDaysSchema,
  }),
  CampaignOutcomeCellSchema.extend({ outcome: z.literal('sold') }),
  CampaignOutcomeCellSchema.extend({ outcome: z.literal('already_sold') }),
  // Not-currently-accepted nośnik excluded from the sale by default
  // (sale-breakdown-not-accepted-units / "Campaign sale endpoint"). No
  // `reason` field: unlike `not_secured`/`not_closed`'s dynamic, only-known-
  // at-runtime explanation, this outcome's reason is static and fully
  // implied by the literal itself.
  CampaignOutcomeCellSchema.extend({ outcome: z.literal('skipped_not_accepted') }),
  CampaignOutcomeCellSchema.extend({ outcome: z.literal('not_closed'), reason: z.string() }),
]);

// Catch-all for an outcome literal this build does not know. Only matches
// when the literal is NOT one of the known outcomes, so a known outcome that
// otherwise fails validation (e.g. `not_secured` missing its `reason`) still
// fails the whole union rather than being quietly swallowed as neutral.
const UnrecognizedCampaignOutcomeItemSchema = CampaignOutcomeCellSchema.extend({
  outcome: z
    .string()
    .refine((value) => !KNOWN_CAMPAIGN_OUTCOMES.has(value), { message: 'Known outcome literal' }),
}).transform(({ mediaUnitId, dateFrom, dateTo }) => ({
  mediaUnitId,
  dateFrom,
  dateTo,
  outcome: 'unrecognized' as const,
}));

export const CampaignOutcomeItemSchema = z.union([
  KnownCampaignOutcomeItemSchema,
  UnrecognizedCampaignOutcomeItemSchema,
]);
export type CampaignOutcomeItem = z.infer<typeof CampaignOutcomeItemSchema>;

export const CampaignOutcomeSummarySchema = z.object({
  results: z.array(CampaignOutcomeItemSchema),
});
export type CampaignOutcomeSummary = z.infer<typeof CampaignOutcomeSummarySchema>;

// Response for POST /offers/:id/approve — the offer plus what confirmation
// secured. A dedicated response schema rather than widening OfferSchema
// itself, so every other endpoint returning OfferSchema (create/update/list/
// cancel/...) is unaffected.
export const ApproveOfferResponseSchema = OfferSchema.extend({
  securing: CampaignOutcomeSummarySchema,
});
export type ApproveOfferResponse = z.infer<typeof ApproveOfferResponseSchema>;

// Response for POST /offers/:id/mark-sold — the offer plus what the sale
// closed. Refused with 409 CAMPAIGN_NOT_CONFIRMED unless the offer is a
// confirmed campaign (campaign-sale / "Campaign sale endpoint").
export const MarkOfferSoldResponseSchema = OfferSchema.extend({
  sale: CampaignOutcomeSummarySchema,
});
export type MarkOfferSoldResponse = z.infer<typeof MarkOfferSoldResponseSchema>;

// Request body for POST /offers/:id/mark-sold (campaign-sale / "Campaign sale
// endpoint"). Opt-in, per call, to include every not-currently-accepted
// nośnik this offer holds a reservation for; defaults to false (excluded).
export const MarkOfferSoldBodySchema = z
  .object({
    includeNotCurrentlyAccepted: z.boolean().optional().default(false),
  })
  .strict();
export type MarkOfferSoldBody = z.infer<typeof MarkOfferSoldBodySchema>;

// Local, unexported period sub-schema for the sale preview response only —
// mirrors UnitDecisionPeriodSchema's {from, to} shape/naming
// (offer-version-unit-decisions.ts), which is not exported from the barrel.
const SalePreviewPeriodSchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date(),
});

// Response for GET /offers/:id/sale-preview (campaign-sale / "Sale
// preview") — every distinct nośnik with an active reservation this offer
// holds, its acceptance state, its full period history, and a single
// dominant reservation status. Not paginated: the preview deliberately
// enumerates active reservations the same uncapped way the sale call itself
// does (proposal.md's "new read endpoint" decision).
export const SalePreviewEntrySchema = z
  .object({
    mediaUnitId: z.number().int().positive(),
    currentlyAccepted: z.boolean(),
    periods: z.array(SalePreviewPeriodSchema),
    reservationStatus: ReservationStatusSchema,
  })
  .strict();
export type SalePreviewEntry = z.infer<typeof SalePreviewEntrySchema>;

export const SalePreviewResponseSchema = z.array(SalePreviewEntrySchema);
export type SalePreviewResponse = z.infer<typeof SalePreviewResponseSchema>;

// Response for the read-only campaign confirmation preview (offer-approval /
// "Campaign confirmation preview"). Reuses `CampaignOutcomeItemSchema`
// directly rather than a preview-specific entry shape, since the preview is
// defined entirely in terms of the shared outcome vocabulary. A flat,
// uncapped array — no wrapper, no pagination — in the same style as
// `SalePreviewResponseSchema` above.
export const ConfirmationPreviewResponseSchema = z.array(CampaignOutcomeItemSchema);
export type ConfirmationPreviewResponse = z.infer<typeof ConfirmationPreviewResponseSchema>;
